"use client"

import { postBlogGetThinLobbyEntry } from "@/api/generated/BMWApi"
import { BlogEntryThinDtoPaginatedList, BlogLobby } from "@/api/generated/BMWInterfaces"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import colors from "@/lib/theme/colors"
import { styled } from "@mui/material"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import Pagination from "../Pagination/Pagination"
import CustomBreadcrumbs from "../shared/CustomBreadcrumbs"
import BlogCategories from "./BlogCategories"
import BlogsGrid from "./BlogsGrid"

const StyledLayout = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 48px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 16px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0 40px;
    max-width: 1160px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    padding: 0 160px;
    max-width: 1920px;
  }
`
const StyledTopWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledHeader = styled("header")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 1080px;
    margin: 0 auto;
  }
`

const StyledHeadline = styled("h1")`
  margin: 0;
  color: ${colors.directBlack};
  font-weight: 400;
  font-size: 24px;
  line-height: 35px;
  margin-bottom: 8px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 32px;
    line-height: 47px;

    text-align: center;
  }
`

const StyledHeaderText = styled("p")`
  margin: 0;
  color: ${colors.directBlack};
  font-size: 16px;
  line-height: 1.5;
  max-width: 760px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    text-align: center;
  }
`

const StyledImageWrapper = styled("div")`
  position: relative;
  width: 100%;
  aspect-ratio: 360 / 212;
  img {
    object-fit: cover;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    aspect-ratio: 1280 / 425;
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    aspect-ratio: 1920 / 425;
  }
`

const StyledGridPaginationWrapper = styled("div")`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 48px;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 40px;
    padding-bottom: 64px;
  }
`

interface BlogsLobbyProps {
  lobby: BlogLobby
  initialEntries?: BlogEntryThinDtoPaginatedList
}

const BlogsLobby = ({ lobby, initialEntries }: BlogsLobbyProps): React.ReactElement => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const gridRef = useRef<HTMLDivElement>(null)
  const entries = initialEntries ?? lobby?.blogEntries
  const [filteredCards, setFilteredCards] = useState(entries?.items)
  const [totalPageCount, setTotalPageCount] = useState(entries?.totalPageCount)

  const categoriesFromUrl = searchParams.get("categories")
  const pageFromUrl = searchParams.get("page")

  const activeCategoryId = categoriesFromUrl
    ? categoriesFromUrl.split(",").map(Number).filter(Boolean)
    : []
  const page = pageFromUrl ? Number(pageFromUrl) : 1

  const fetchEntries = useCallback((categoryIds: number[], requestPage: number) => {
    postBlogGetThinLobbyEntry({
      categoryIds,
      requestPage: requestPage - 1,
    })
      .then((response) => {
        setFilteredCards(response.data.body?.items || [])
        setTotalPageCount(response.data.body?.totalPageCount ?? 0)
      })
      .catch((error) => {
        console.error("Error fetching thin lobby entries:", error)
      })
  }, [])

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const categoryIds = categoriesFromUrl
      ? categoriesFromUrl.split(",").map(Number).filter(Boolean)
      : []
    const currentPage = pageFromUrl ? Number(pageFromUrl) : 1
    const hasFilters = categoryIds.length > 0 || currentPage > 1
    if (hasFilters) {
      fetchEntries(categoryIds, currentPage)
    } else {
      setFilteredCards(lobby?.blogEntries?.items)
      setTotalPageCount(lobby?.blogEntries?.totalPageCount)
    }
  }, [
    categoriesFromUrl,
    pageFromUrl,
    fetchEntries,
    lobby?.blogEntries?.items,
    lobby?.blogEntries?.totalPageCount,
  ])

  const updateSearchParams = (params: Record<string, string | null>, replace = false): void => {
    const newParams = new URLSearchParams(searchParams.toString())
    Object.entries(params).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key)
      } else {
        newParams.set(key, value)
      }
    })
    const query = newParams.toString()
    const url = query ? `/blog?${query}` : "/blog"
    if (replace) {
      router.replace(url, { scroll: false })
    } else {
      router.push(url, { scroll: false })
    }
  }

  const handleCategoryChange = (categoryIds: number[]): void => {
    if (categoryIds.length > 0) {
      fetchEntries(categoryIds, 1)
    } else {
      setFilteredCards(lobby?.blogEntries?.items)
      setTotalPageCount(lobby?.blogEntries?.totalPageCount)
    }
    updateSearchParams(
      {
        categories: categoryIds.length > 0 ? categoryIds.join(",") : null,
        page: null,
      },
      true,
    )
  }

  const handlePageChange = (newPage: number): void => {
    fetchEntries(activeCategoryId, newPage)
    updateSearchParams({ page: newPage > 1 ? String(newPage) : null })
    if (gridRef.current) {
      const offset = window.innerWidth >= 768 ? 180 : 130
      const top = gridRef.current.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: "smooth" })
    }
  }
  return (
    <>
      <Desktop>
        {lobby?.lobbyImage?.imageUrl && (
          <StyledImageWrapper>
            <Image
              src={lobby.lobbyImage.imageUrl}
              alt={lobby.lobbyImage.alt || "Lobby Image"}
              layout="fill"
            />
          </StyledImageWrapper>
        )}
      </Desktop>
      <Mobile>
        {lobby?.lobbyImageMobile?.imageUrl && (
          <StyledImageWrapper>
            <Image
              src={lobby.lobbyImageMobile.imageUrl}
              alt={lobby.lobbyImageMobile.alt || "Lobby Image"}
              layout="fill"
            />
          </StyledImageWrapper>
        )}
      </Mobile>
      <StyledLayout>
        <StyledTopWrapper>
          <CustomBreadcrumbs
            current={{ title: "בלוג", url: "/blog" }}
            parents={[{ title: "ראשי", url: "/" }]}
          />
          <StyledHeader>
            {lobby.title && <StyledHeadline>{lobby.title}</StyledHeadline>}
            {lobby.subTitle && <StyledHeaderText>{lobby.subTitle}</StyledHeaderText>}
          </StyledHeader>
        </StyledTopWrapper>

        <BlogCategories
          categories={lobby?.blogCategories}
          activeCategoryId={activeCategoryId}
          onCategoryChange={handleCategoryChange}
        />
        <StyledGridPaginationWrapper ref={gridRef}>
          <BlogsGrid
            blogs={filteredCards}
            categories={lobby?.blogCategories}
            pageSize={entries?.pageSize || 6}
          />
          {totalPageCount > 1 && (
            <Pagination count={totalPageCount} page={page} onChange={handlePageChange} />
          )}
        </StyledGridPaginationWrapper>
      </StyledLayout>
    </>
  )
}

export default BlogsLobby
